/**
 * @file fieldNamingUtils.js
 * @description Pure utility functions for parsing Client Option 13 (Field Naming)
 * API responses into lookup maps used to resolve client-customized field labels.
 */

const FIELD_NAMING_OPTION_NAME = 'Field Naming';

/**
 * Extracts the Field Naming option object from a client options API response.
 * @param {Array|Object} response - The raw API response (array or wrapped object).
 * @returns {Object|undefined} The Field Naming option object, if found.
 */
const parseFieldNamingResponse = response => {
    const array = Array.isArray(response)
        ? response
        : response?.clientOptions || [];
    return array.find(item => item.name === FIELD_NAMING_OPTION_NAME);
};

/**
 * Builds a map of internal field key → custom display label.
 * @param {Array<Object>} fieldNamingJSON - Parsed JSON value from the Field Naming option.
 * @returns {Object} Map of { internalKey: customLabel }.
 */
const buildFieldNamingMap = fieldNamingJSON => {
    const map = {};
    fieldNamingJSON.forEach(obj => {
        const key = Object.keys(obj)[0];
        const value = obj[key];
        map[key] = value;
    });
    return map;
};

/**
 * Builds a reverse map of default display label → internal field key.
 * @param {Array<Object>} defaultFieldNamingJSON - Parsed JSON value from the default Field Naming option.
 * @returns {Object} Map of { defaultLabel: internalKey }.
 */
const buildDefaultFieldNamingMap = defaultFieldNamingJSON => {
    const map = {};
    defaultFieldNamingJSON.forEach(obj => {
        const key = Object.keys(obj)[0];
        const value = obj[key];
        map[value] = key;
    });
    return map;
};

export {
    parseFieldNamingResponse,
    buildFieldNamingMap,
    buildDefaultFieldNamingMap,
};
